"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion } from "motion/react";
import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP } from "../lib/onyx-material";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { IconButton } from "../primitives/IconButton";
import { Button } from "../primitives/Button";
import { Spinner } from "../primitives/Spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../primitives/Tooltip";
import {
  DEFAULT_OAUTH_PROVIDERS,
  type OAuthProviderConfig,
} from "./AuthModal";
import { transitions } from "../lib/motion";

/**
 * OAuthProviderButtons — one reusable social sign-in row.
 *
 * The "row of provider tiles that calls back with a provider id" was rebuilt
 * three times across the apps (fe-react's `SocialAuthButtons`, AuthModal's
 * inlined grid, unified's bespoke `AuthScreen` grid), each with a different
 * icon source and layout. This is the single branded version. It maps over the
 * canonical `OAuthProviderConfig[]` (default `DEFAULT_OAUTH_PROVIDERS`, both
 * re-exported here from `./AuthModal` so there is one definition) and renders
 * one tile per provider.
 *
 * Composition (we do not hand-roll a button):
 * - `appearance="icon"` (default) renders the `IconButton` primitive as a
 *   rectangular brand tile in the Onyx "offset key" language: the `nx-m` notch
 *   silhouette, a continuous `nx-rim` keyline (sliced-border-safe, keyed to
 *   `--border` so it stays subtle, #158), and the springy 2px diagonal press
 *   onto a solid travel block sitting down-right behind the face. We override
 *   IconButton's round resting surface with `provider.color` / `provider.hoverColor`
 *   (the brand-token classes already on each config) and own the press here, so
 *   IconButton's own round 1px press is disabled (`motion={false}`) and never doubles.
 * - `appearance="labeled"` renders `Button variant="outline"` with
 *   `{verb} {label}` text (fe-react's full-width shape) — the flat outline key
 *   (notch + ring + 1px press-down) it now carries by default.
 *
 * Logic stays with the consumer: `onSelect(id)` mirrors AuthModal's
 * `onOAuthClick`. The caller threads any referral / extra value into its own
 * handler (`onSelect={(id) => SOCIAL[id](referralQuery)}`), so this component
 * holds no env vars and builds no OAuth URLs (same separation AuthModal keeps).
 *
 * Motion (on by default, all reduced-motion safe via `useReducedMotion`): the
 * row staggers its tiles in on mount, each icon tile keeps its offset-key press
 * (springy 2px diagonal onto its travel block) + hover-lift, and a single
 * provider can morph its icon to a `Spinner` (`loadingProviderId`) without
 * relayout (fixed tile size). `motion={false}` keeps the depth but freezes the press.
 *
 * @example
 *   // AuthModal grid (4 icon tiles)
 *   <OAuthProviderButtons onSelect={onOAuthClick} disabled={isSubmitting} />
 *
 * @example
 *   // unified AuthScreen (Google / Microsoft / GitHub, three columns)
 *   <OAuthProviderButtons
 *     providers={[google, microsoft, github]}
 *     columns={3}
 *     disabled={busy || checking}
 *     onSelect={(id) => SOCIAL[id as SsoProvider]()}
 *   />
 *
 * @example
 *   // fe-react full-width labeled buttons
 *   <OAuthProviderButtons
 *     appearance="labeled"
 *     layout="row"
 *     mode="signup"
 *     onSelect={(id) => providers[id].login(referralQuery)}
 *   />
 */

/* ============================================================================
 * Re-exported config — single source of truth lives in `./AuthModal`. Re-export
 * here (do not redefine) so consumers can import the shape + defaults alongside
 * the component.
 * ========================================================================= */
export { DEFAULT_OAUTH_PROVIDERS, type OAuthProviderConfig } from "./AuthModal";

/** Verb prefix driven by `mode`, used in tile aria-label / title / tooltip / label. */
const MODE_VERB: Record<NonNullable<OAuthProviderButtonsProps["mode"]>, string> = {
  login: "Log in with",
  signup: "Sign up with",
};

export const oauthProviderButtonsVariants = cva("w-full", {
  variants: {
    layout: {
      /** CSS grid; column count driven by the `columns` prop. Reproduces AuthModal. */
      grid: "grid gap-2",
      /** Flex row of equal-width tiles. Reproduces fe-react's full-width buttons. */
      row: "flex gap-3",
    },
  },
  defaultVariants: {
    layout: "grid",
  },
});

export interface OAuthProviderButtonsProps
  extends Omit<
      React.HTMLAttributes<HTMLDivElement>,
      "onSelect" | "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag"
    >,
    VariantProps<typeof oauthProviderButtonsVariants> {
  /**
   * Providers to render. Defaults to `DEFAULT_OAUTH_PROVIDERS` (Google, GitHub,
   * Discord, Microsoft). Pass a subset / reorder to show only some (this is how
   * unified shows Google / Microsoft / GitHub and how Discord stays optional).
   */
  providers?: OAuthProviderConfig[];
  /** Called with the provider id when a tile is activated. Mirrors AuthModal's `onOAuthClick`. */
  onSelect: (providerId: string) => void;
  /** Tile appearance: icon-only (`IconButton`) or full-width labeled (`Button variant="outline"`). Default "icon". */
  appearance?: "icon" | "labeled";
  /** Grid columns when `layout="grid"`. Default 4. Ignored for `layout="row"`. */
  columns?: number;
  /**
   * Verb prefix for the accessible label / title / tooltip / labeled text
   * ("Sign in with" by default). Use `mode` for the fe-react verbs, or pass
   * `verb` directly to override both.
   */
  mode?: "login" | "signup";
  /** Explicit verb prefix; overrides `mode`. Default "Sign in with". */
  verb?: string;
  /** Global busy flag — disables every tile (maps to unified's `busy || checking` and AuthModal's `isSubmitting`). */
  disabled?: boolean;
  /** Render the per-tile loading morph (icon -> Spinner) for this provider id. */
  loadingProviderId?: string;
  /** Wrap each tile in the brand Tooltip showing `{verb} {label}`. Default true. */
  showTooltip?: boolean;
  /** Map a provider id to its `data-testid`. Default `social-login-${id}` (keeps existing e2e selectors). */
  getTestId?: (providerId: string) => string;
  /** Stagger the tiles in on mount. Default true. Honors `prefers-reduced-motion`. */
  animated?: boolean;
  /** Disable per-tile press-spring (forwarded to the primitive). Default true. */
  motion?: boolean;
}

const defaultTestId = (id: string) => `social-login-${id}`;

// The Onyx offset-key press for a solid brand tile (a rectangular face, so it
// takes the 2px diagonal press onto a real travel block — same recipe Button's
// secondary/destructive keys use). Bounce is the shared springy overshoot.
const TILE_BOUNCE = "transition-transform duration-150 ease-[cubic-bezier(0.34,1.45,0.6,1)]";
const TILE_PRESS = "active:translate-x-[2px] active:translate-y-[2px]";

export const OAuthProviderButtons = React.forwardRef<
  HTMLDivElement,
  OAuthProviderButtonsProps
>(
  (
    {
      providers = DEFAULT_OAUTH_PROVIDERS,
      onSelect,
      appearance = "icon",
      layout = "grid",
      columns = 4,
      mode,
      verb,
      disabled = false,
      loadingProviderId,
      showTooltip = true,
      getTestId = defaultTestId,
      animated = true,
      motion: enableMotion = true,
      className,
      style,
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const shouldAnimate = reduced !== true;
    const verbText = verb ?? (mode ? MODE_VERB[mode] : "Sign in with");

    // Only `layout="grid"` honors `columns`; an inline template keeps the count
    // dynamic without relying on JIT class generation in consumer builds (the
    // same approach FeatureTabs uses).
    const gridStyle =
      layout === "grid"
        ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, ...style }
        : style;

    return (
      <div
        ref={ref}
        data-slot="oauth-provider-buttons"
        className={cn(oauthProviderButtonsVariants({ layout }), className)}
        style={gridStyle}
        {...rest}
      >
        {providers.map((provider, index) => {
          const isConfigured = provider.configured !== false;
          const isLoading = loadingProviderId === provider.id;
          const isDisabled = !isConfigured || disabled || isLoading;
          const label = `${verbText} ${provider.label}`;
          const title = isConfigured
            ? label
            : `${provider.label} login is not configured`;

          // Press-spring stays on per tile; mount-stagger is opt-in via `animated`.
          const tileMotion = enableMotion && shouldAnimate;

          const tile =
            appearance === "labeled" ? (
              <Button
                type="button"
                variant="outline"
                motion={tileMotion}
                isLoading={isLoading}
                disabled={isDisabled}
                onClick={() => onSelect(provider.id)}
                title={title}
                aria-label={label}
                data-slot="oauth-provider-button"
                data-testid={getTestId(provider.id)}
                className="w-full"
              >
                {!isLoading ? <span aria-hidden>{provider.icon}</span> : null}
                <span>{label}</span>
              </Button>
            ) : (
              // A solid brand tile is a rectangular Onyx key: a real travel block
              // sits down-right BEHIND the face (a sibling, so the notch clip-path
              // can't slice it the way it would a box-shadow) and the face presses
              // 2px diagonally onto it. The face itself is the IconButton with the
              // `nx-m` notch + a continuous keyline ring.
              <span className="relative inline-flex w-full">
                <span
                  aria-hidden
                  className={cn(
                    nx.m,
                    "pointer-events-none absolute inset-0 z-0 translate-x-[2px] translate-y-[2px] bg-[#06101e] dark:bg-[#0a2a5e]",
                  )}
                />
                <IconButton
                  // We own the press here (2px diagonal onto the block), so disable
                  // IconButton's own round 1px press-down to avoid two presses.
                  motion={false}
                  disabled={isDisabled}
                  onClick={() => onSelect(provider.id)}
                  title={title}
                  aria-label={label}
                  data-slot="oauth-provider-button"
                  data-testid={getTestId(provider.id)}
                  className={cn(
                    // Provider tiles carry their own brand surface, so override
                    // IconButton's borderless round style. The Onyx notch makes
                    // it a rectangular tile face; match AuthModal's tile sizing.
                    "relative z-[1] h-auto w-full p-2.5",
                    nx.m,
                    // Clip-safe focus: IconButton's own `ring-*` focus is a box-shadow
                    // the notch slices, so add a drop-shadow glow that survives the clip
                    // (onyx-material rule #2), matching the other Onyx keys.
                    "focus-visible:[filter:drop-shadow(0_0_1px_hsl(var(--ring)))_drop-shadow(0_0_3px_hsl(var(--ring)/0.55))]",
                    // Neutralize IconButton's own hover (bg-accent/10 +
                    // text-accent-foreground). On a dark tile like GitHub
                    // (bg-foreground surface, text-background icon via currentColor)
                    // that flip turned the surface light and the icon dark, so the
                    // mark vanished on hover. hover:text-inherit keeps the resting
                    // text color (so each provider's brand icon color stays driving)
                    // and we let provider.hoverColor own the hover surface, so the
                    // global override never forces the tile transparent.
                    "hover:-translate-y-0.5 hover:text-inherit",
                    "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0",
                    // The shared springy press onto the travel block (gated by motion).
                    tileMotion && TILE_BOUNCE,
                    tileMotion && TILE_PRESS,
                    provider.color,
                    provider.hoverColor,
                  )}
                >
                  {/* Continuous keyline on the SAME notch (NX_RING_CLIP) so the
                      rim traces the chamfer instead of being sliced at the diagonal
                      (#158). Kept subtle via `--border`; provider.color's own border
                      is sliced by the clip but this overlay restores the silhouette. */}
                  <span
                    aria-hidden
                    className="nx-rim pointer-events-none absolute inset-0"
                    style={
                      {
                        "--nx-rim": "hsl(var(--border))",
                        clipPath: NX_RING_CLIP.m,
                      } as React.CSSProperties
                    }
                  />
                  {isLoading ? (
                    <Spinner size="default" decorative />
                  ) : (
                    provider.icon
                  )}
                </IconButton>
              </span>
            );

          const content = showTooltip ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>{tile}</TooltipTrigger>
                <TooltipContent>{title}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            tile
          );

          // Each tile sits in its own cell wrapper so the grid/flex layout and
          // (when enabled) the mount-stagger transform attach to a real box.
          const cellClass = cn("flex", layout === "row" && "flex-1");

          return animated && shouldAnimate ? (
            <motion.div
              key={provider.id}
              className={cellClass}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...transitions.surface, delay: index * 0.04 }}
            >
              {content}
            </motion.div>
          ) : (
            <div key={provider.id} className={cellClass}>
              {content}
            </div>
          );
        })}
      </div>
    );
  },
);

OAuthProviderButtons.displayName = "OAuthProviderButtons";
